import { useEffect, useRef } from 'react';
import { View, Text, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import {
  wordleTileStyles as styles,
  wordleTileBorderStyles,
  wordleTileLedgeStyles,
  wordleTileTextStyles,
} from './WordleTile.styles';
import { WORDLE_CHIP_COLORS, type WordleTileState } from '../../../src/quiz/constants/warmup.constants';
import {
  tileFaceSizeStyle,
  tileOuterSizeStyle,
  tileTextSizeStyle,
} from '../../../src/quiz/helpers/gameDynamicStyles.helper';

export interface BoardTile {
  letter: string;
  state: WordleTileState;
}

interface WordleTileProps {
  tile: BoardTile;
  size: number;
  index: number;
  /**
   * True when this tile is mounting as part of a freshly-committed row, so it should start hidden
   * and pop in (staggered by column). Tiles that are already on the board — including rows a resumed
   * game loads with — mount with this false and render fully visible, no animation.
   */
  revealOnMount: boolean;
}

const REVEAL_STAGGER_MS = 80;

/** A single chip tile: a gradient face on a 3px ledge, with pop (reveal) / bump (type) animation. */
export function WordleTile({ tile, size, index, revealOnMount }: WordleTileProps) {
  const colors = WORDLE_CHIP_COLORS[tile.state];
  // Initialise hidden when this is a reveal mount so the first paint is already small + transparent
  // (prevents the one-frame flash of the final colours before the animation starts).
  const scale = useRef(new Animated.Value(revealOnMount ? 0.4 : 1)).current;
  const opacity = useRef(new Animated.Value(revealOnMount ? 0 : 1)).current;

  // Reveal: pop the tile in, staggered left → right across its row.
  useEffect(() => {
    if (!revealOnMount) return;
    Animated.parallel([
      Animated.sequence([
        Animated.delay(index * REVEAL_STAGGER_MS),
        Animated.spring(scale, { toValue: 1, friction: 5, tension: 140, useNativeDriver: true }),
      ]),
      Animated.sequence([
        Animated.delay(index * REVEAL_STAGGER_MS),
        Animated.timing(opacity, { toValue: 1, duration: 140, useNativeDriver: true }),
      ]),
    ]).start();
  }, [revealOnMount, index, scale, opacity]);

  // Type bump: a quick squash-in when a letter is entered into the active (uncommitted) row.
  useEffect(() => {
    if (revealOnMount || tile.state !== 'filled' || !tile.letter) return;
    scale.setValue(0.82);
    Animated.spring(scale, { toValue: 1, friction: 5, tension: 240, useNativeDriver: true }).start();
  }, [tile.letter, tile.state, revealOnMount, scale]);

  return (
    <Animated.View
      style={[
        styles.outer,
        tileOuterSizeStyle(size),
        wordleTileLedgeStyles[tile.state],
        { opacity, transform: [{ scale }] },
      ]}
    >
      <View style={[styles.faceArea, tileFaceSizeStyle(size)]}>
        <LinearGradient
          colors={[colors.top, colors.mid]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={[styles.faceGradient, wordleTileBorderStyles[tile.state]]}
        />
        <Text style={[styles.text, wordleTileTextStyles[tile.state], tileTextSizeStyle(size)]}>{tile.letter}</Text>
      </View>
    </Animated.View>
  );
}
